import { useQuery } from "@tanstack/react-query";

import { flattenPhotos, type ComparePhoto } from "@fitness-app/client-shared/features/measurements/compare-photo";
import { MeasurementType, type ClientPhoto } from "@fitness-app/data-models/entities/Measurement";

import { traineeMeasurementKeys } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeMeasurements/useTraineeMeasurementsQueries";
import { supabase } from "~/store/initializeStore";

/** PostgREST caps a response at 1000 rows, so longer histories are read in chunks. */
const CHUNK_SIZE = 1000;

/**
 * TRE-2290 — every body photo of the client, newest first. The list on the tab pages through
 * 25 sets at a time, but the comparison is mostly "first photo vs latest", so the modal needs the
 * whole history regardless of how far the trainer has scrolled.
 */
export const useAllTraineeBodyPhotos = (traineeId: string, enabled: boolean) =>
  useQuery(traineeMeasurementKeys.allBodyPhotos(traineeId), {
    enabled,
    queryFn: async (): Promise<ComparePhoto[]> => {
      const measurements: ClientPhoto[] = [];
      for (let start = 0; ; start += CHUNK_SIZE) {
        const { data, error } = await supabase
          .from("measurement")
          .select("*")
          .eq("traineeId", traineeId)
          .eq("type", MeasurementType.ClientPhoto)
          .order("eventTimestamp", { ascending: false })
          .order("id")
          .range(start, start + CHUNK_SIZE - 1)
          .returns<ClientPhoto[]>();
        if (error) {
          throw new Error(error.message);
        }
        measurements.push(...(data ?? []));
        if (!data || data.length < CHUNK_SIZE) {
          break;
        }
      }
      return flattenPhotos(measurements);
    },
  });
